import type { CSSProperties, ReactNode } from 'react';

import Link from 'next/link';

import { cn } from '@naumu/ui/utils/cn';

import { ArrowLink } from './arrow-link';

// ---------------------------------------------------------------------------
// marketing-nav-panel-card - the shared anatomy of every designed header
// popover body (Product / Use cases / Resources): an inner hairline card
// holding grey-label + white-benefit cells on a quiet surface fill, then a
// footer row OUTSIDE the card - quiet contextual note left, the landing's
// neutral arrow link right. Composing panels supply the cell arrangement
// (row-major grid or group columns); these atoms keep the three popovers
// pixel-identical in language.
// ---------------------------------------------------------------------------

export interface NavPanelCell {
	/** Grey one-word-ish label over the benefit line. */
	label: string;
	/** Optional muted mono spine number before the label ("1.0" - the stage grammar). */
	prefix?: string;
	/** The white benefit line under the label. */
	benefit: string;
	href: string;
}

/** One panel cell: grey label over the white two-line benefit, the whole cell a link. */
export const NavPanelCellLink = ({
	cell,
	className,
	style,
}: {
	cell: NavPanelCell;
	className?: string;
	style?: CSSProperties;
}) => (
	<Link
		href={cell.href}
		role="menuitem"
		style={style}
		className={cn('block px-4 py-3.5 transition-colors hover:bg-white/[0.04]', className)}
	>
		<span className="flex items-baseline gap-1.5 text-[12px] font-medium text-white/40">
			{cell.prefix && (
				<span className="font-mono text-white/30 [font-feature-settings:'tnum'_1,'zero'_1]">
					{cell.prefix}
				</span>
			)}
			{cell.label}
		</span>
		<span className="mt-1 block text-[13px] leading-snug text-white">{cell.benefit}</span>
	</Link>
);

/**
 * The inner card: the DS surface token behind the hairline (the marketing
 * shell forces `.dark`, so this is the raised dark surface), so the card
 * reads as a lifted section over the popover's page-black canvas.
 */
export const NavPanelCard = ({ children }: { children: ReactNode }) => (
	<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-surface/50">{children}</div>
);

/** The footer row outside the inner card: note left, neutral arrow link right. */
export const NavPanelFooterRow = ({
	note,
	linkLabel,
	linkHref,
}: {
	note: ReactNode;
	linkLabel: string;
	linkHref: string;
}) => (
	<div className="flex items-center justify-between gap-3 px-3 pb-1.5 pt-3">
		<span className="min-w-0 truncate text-[12.5px] leading-[1.4] text-white/45">{note}</span>
		<ArrowLink href={linkHref} label={linkLabel} size="sm" role="menuitem" className="shrink-0" />
	</div>
);
